import client from "./client";

export type AnnouncementCategory =
  | "general"
  | "policy"
  | "event"
  | "welcome"
  | "urgent";
export type AnnouncementPriority = "normal" | "high" | "urgent";
export type AnnouncementStatus = "draft" | "scheduled" | "published" | "expired";

export interface AnnouncementAuthor {
  id: string;
  name: string;
}

export interface Announcement {
  id: string;
  title: string;
  body: string;
  category: AnnouncementCategory;
  priority: AnnouncementPriority;
  is_pinned: boolean;
  published_at: string | null;
  scheduled_for: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  author: AnnouncementAuthor | null;
  target_departments: string[] | null;
  target_roles: string[] | null;
  attachment_url: string | null;
  attachment_name: string | null;
  is_read: boolean;
  status: AnnouncementStatus;
}

export interface AnnouncementCreateInput {
  title: string;
  body: string;
  category?: AnnouncementCategory;
  priority?: AnnouncementPriority;
  is_pinned?: boolean;
  scheduled_for?: string | null;
  expires_at?: string | null;
  publish_now?: boolean;
  target_departments?: string[] | null;
  target_roles?: string[] | null;
  attachment_url?: string | null;
  attachment_name?: string | null;
}

export interface AnnouncementUpdateInput
  extends Partial<Omit<AnnouncementCreateInput, "publish_now">> {
  publish?: boolean | null;
}

export interface AnnouncementReadReceipt {
  user_id: string;
  name: string;
  email: string | null;
  read_at: string;
}

export interface ReadReceiptsSummary {
  announcement_id: string;
  total_eligible: number;
  total_read: number;
  percent_read: number;
  reads: AnnouncementReadReceipt[];
  unread_user_ids: string[];
}

export async function listAnnouncements(
  includeDrafts = false,
): Promise<Announcement[]> {
  const { data } = await client.get("/announcements", {
    params: { include_drafts: includeDrafts },
  });
  return data;
}

export async function getAnnouncement(
  id: string,
  markRead = true,
): Promise<Announcement> {
  const { data } = await client.get(`/announcements/${id}`, {
    params: { mark_read: markRead },
  });
  return data;
}

export async function createAnnouncement(
  input: AnnouncementCreateInput,
): Promise<Announcement> {
  const { data } = await client.post("/announcements", input);
  return data;
}

export async function updateAnnouncement(
  id: string,
  input: AnnouncementUpdateInput,
): Promise<Announcement> {
  const { data } = await client.put(`/announcements/${id}`, input);
  return data;
}

export async function deleteAnnouncement(id: string): Promise<void> {
  await client.delete(`/announcements/${id}`);
}

export async function markAnnouncementRead(id: string): Promise<number> {
  const { data } = await client.post(`/announcements/${id}/read`);
  return data.count;
}

export async function getUnreadAnnouncementCount(): Promise<number> {
  const { data } = await client.get("/announcements/unread-count");
  return data.count;
}

export async function getAnnouncementReadReceipts(
  id: string,
): Promise<ReadReceiptsSummary> {
  const { data } = await client.get(`/announcements/${id}/reads`);
  return data;
}

export async function remindUnreadAnnouncement(
  id: string,
): Promise<number> {
  const { data } = await client.post(`/announcements/${id}/remind-unread`);
  return data.reminded;
}
